import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setTopNavbar } from '../../Reducers/actions/navbarActions';
import {
    getShippingAddresses,
    createShippingAddress,
    updateShippingAddress,
    deleteShippingAddress,
    setMainShippingAddress,
    openShippingAddressForm,
    closeShippingAddressForm
} from '../../Reducers/actions/shippingAddressActions';
import Icon from '../../components/Icon/Icon';
import ModalMessage from '../../components/ModalMessage/ModalMessage';
import Skeleton from '../../components/Skeleton/Skeleton';
import './ShippingAddresses.scss';
import AddShipping from '../../components/AddShipping/AddShipping';

function ShippingAddressesListSkeleton() {
    return (
        <div className="shipping-addresses-container" role="status" aria-label="Loading addresses">
            <div className="shipping-addresses-list px-16">
                {[1, 2].map((i) => (
                    <div
                        key={i}
                        className="shipping-address-card c-pointer"
                        style={{ pointerEvents: 'none' }}
                        aria-hidden
                    >
                        <div className="address-content">
                            <Skeleton width="132px" height="14px" borderRadius="6px" className="m-0 p-0 mb-8" />
                            <Skeleton width="100%" height="12px" borderRadius="6px" className="m-0 p-0 mb-4" />
                            <Skeleton width="88%" height="12px" borderRadius="6px" className="m-0 p-0" />
                        </div>
                        <Skeleton width="40px" height="40px" borderRadius="12px" className="m-0 p-0 mr-16" />
                    </div>
                ))}
            </div>
        </div>
    );
}

const ShippingAddresses = () => {
    const dispatch = useDispatch();
    const { addresses, loading, showForm } = useSelector((state) => state.shippingAddress || { addresses: [], loading: false, showForm: false });
    const [editingAddress, setEditingAddress] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [addressToDelete, setAddressToDelete] = useState(null);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        country: 'France',
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        dispatch(setTopNavbar({
            title: 'SHIPPING ADDRESSES',
            showBackArrow: true,
            showAddButton: true,
        }));
    }, [showForm, editingAddress, dispatch]);

    useEffect(() => {
        dispatch(getShippingAddresses());
    }, [dispatch]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error for this field
        if (formErrors[field]) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.first_name.trim()) errors.first_name = 'First name is required';
        if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
        if (!formData.country.trim()) errors.country = 'Country is required';
        if (!formData.address_line_1.trim()) errors.address_line_1 = 'Address line 1 is required';
        if (!formData.city.trim()) errors.city = 'City is required';
        if (!formData.postal_code.trim()) errors.postal_code = 'Postal code is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const payload = {
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                country: formData.country.trim(),
                address_line_1: formData.address_line_1.trim(),
                address_line_2: formData.address_line_2.trim() || null,
                city: formData.city.trim(),
                postal_code: formData.postal_code.trim()
            };

            if (editingAddress) {
                const result = await dispatch(updateShippingAddress(editingAddress.address_id, payload));
                if (result?.success) {
                    dispatch(closeShippingAddressForm());
                    setEditingAddress(null);
                    resetForm();
                    dispatch(getShippingAddresses());
                }
            } else {
                const result = await dispatch(createShippingAddress(payload));
                if (result?.success) {
                    dispatch(closeShippingAddressForm());
                    resetForm();
                    dispatch(getShippingAddresses());
                }
            }
        } catch (error) {
            console.error('Error saving address:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            country: 'France',
            address_line_1: '',
            address_line_2: '',
            city: '',
            postal_code: ''
        });
        setFormErrors({});
    };

    const handleEdit = (address) => {
        setEditingAddress(address);
        dispatch(openShippingAddressForm());
    };

    const handleDelete = (address) => {
        setAddressToDelete(address);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!addressToDelete) return;
        try {
            const result = await dispatch(deleteShippingAddress(addressToDelete.address_id));
            if (result?.success) {
                setShowDeleteModal(false);
                setAddressToDelete(null);
                dispatch(getShippingAddresses());
            }
        } catch (error) {
            console.error('Error deleting address:', error);
        }
    };

    const handleSetMain = async (addressId) => {
        try {
            await dispatch(setMainShippingAddress(addressId));
            dispatch(getShippingAddresses());
        } catch (error) {
            console.error('Error setting main address:', error);
        }
    };

    if (loading && !addresses?.length) {
        return <ShippingAddressesListSkeleton />;
    }

    // Show form view
    if (showForm) {
        return (
            <>
                <ModalMessage onClose={() => dispatch(closeShippingAddressForm())}>
                    <AddShipping 
                        editingAddress={editingAddress} 
                        setEditingAddress={setEditingAddress} 
                        handleDelete={handleDelete} 
                        onSuccess={() => dispatch(closeShippingAddressForm())}
                    />
                </ModalMessage>
            </>
        );
    }

    // Show empty state or list view
    return (
        <div className="shipping-addresses-container ">
            {!addresses || addresses.length === 0 ? (
                <div className="shipping-addresses-empty">
                    <div className="empty-state-icon">
                        <Icon alias="house" color="#FF3232" width={120} height={120} />
                    </div>
                    <div className="empty-state-box">
                        <p className="fs-regular fw-bold fg-dark mb-8">Add a shipping address</p>
                        <p className="fs-xs fg-dark fw-medium">
                            Addresses to receive your physical purchases
                        </p>
                    </div>
                    <button
                        className="btn red-btn btn-full"
                        onClick={() => dispatch(openShippingAddressForm())}
                        style={{ height: '64px', marginTop: '24px' }}
                    >
                        ADD A SHIPPING ADDRESS
                    </button>
                </div>
            ) : (
                <div className="shipping-addresses-list ">
                    {/* <div className="shipping-addresses-header">
                        <h2 className="fs-regular fw-bold fg-dark">SHIPPING ADDRESS</h2>
                        <Icon
                            alias="box-add"
                            width={24}
                            height={24}
                            color="#1C1B1F"
                            className="c-pointer"
                            onClick={() => {
                                resetForm();
                                setShowForm(true);
                            }}
                        />
                    </div> */}
                    {addresses?.map((address) => (
                        <div
                            key={address.address_id}
                            className={`shipping-address-card ${address.is_main ? 'main-address' : ''} c-pointer`}
                            onClick={() => !address.is_main && handleSetMain(address.address_id)}
                        >
                            <div className="address-content">
                                <p className={`address-label ${address.is_main ? 'fg-white' : 'fg-dark'} fw-bold mb-8`}>
                                    {address.is_main ? 'Main address' : 'Other address'}
                                </p>
                                <p className={`address-text ${address.is_main ? 'fg-white' : 'fg-dark'} fs-xs`}>
                                    {address.address_line_1}
                                    {address.address_line_2 && `, ${address.address_line_2}`}
                                    {address.city && `, ${address.city}`}
                                    {address.postal_code && ` ${address.postal_code}`}
                                    {address.country && `, ${address.country}`}
                                </p>
                            </div>
                            <div
                                className="bg-bg-grey icon-container mr-16 d-flex aic jcc c-pointer"
                                onClick={() => {
                                    if (location.pathname.includes("/shipping-addresses")) {
                                        dispatch(openShippingAddressForm());
                                    } else if (location.pathname.includes("/shipping-methods")) {
                                        dispatch(openShippingMethodForm());
                                    }
                                }}
                            >
                                <Icon
                                    alias="edit_shipping_address"
                                    width={18}
                                    height={18}
                                    color={ '#FFF' }
                                    stroke={'#FF3232'}
                                    strokeWidth={2}
                                    className="c-pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(address);
                                    }}
                                />
                            </div>

                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ShippingAddresses;

